import { authApiEndpoints } from './modules/auth.api'
import { activityLogApiEndpoints } from './modules/activity-log.api'
import { areaPricingApiEndpoints } from './modules/area-pricing.api'
import { branchApiEndpoints } from './modules/branch.api'
import { bookingApiEndpoints } from './modules/booking.api'
import { billiardLightApiEndpoints } from './modules/billiard-light.api'
import { cashBookApiEndpoints } from './modules/cash-book.api'
import { cashTransactionApiEndpoints } from './modules/cash-transaction.api'
import { dashboardApiEndpoints } from './modules/dashboard.api'
import { deliveryPartnerApiEndpoints } from './modules/delivery-partner.api'
import { inventoryApiEndpoints } from './modules/inventory.api'
import { manufacturingApiEndpoints } from './modules/manufacturing.api'
import { offlinePosApiEndpoints } from './modules/offline-pos.api'
import { customerApiEndpoints } from './modules/customer.api'
import { paymentMethodApiEndpoints } from './modules/payment-method.api'
import { permissionApiEndpoints } from './modules/permission.api'
import { priceApiEndpoints } from './modules/price.api'
import { productApiEndpoints } from './modules/product.api'
import { roleApiEndpoints } from './modules/role.api'
import { salesAreaApiEndpoints } from './modules/sales-area.api'
import { salesInvoiceApiEndpoints } from './modules/sales-invoice.api'
import { saleReturnApiEndpoints } from './modules/sale-return.api'
import { salesOrderApiEndpoints } from './modules/sales-order.api'
import { salesTableApiEndpoints } from './modules/sales-table.api'
import { staffApiEndpoints } from './modules/staff.api'
import { supplierApiEndpoints } from './modules/supplier.api'
import { systemSettingApiEndpoints } from './modules/system-setting.api'
import { printTemplateApiEndpoints } from './modules/print-template.api'
import { tablePricingApiEndpoints } from './modules/table-pricing.api'
import { tableSessionApiEndpoints } from './modules/table-session.api'
import { voucherApiEndpoints } from './modules/voucher.api'
import { shiftRevenueReportApiEndpoints } from './modules/shift-revenue-report.api'
import { shiftClosingReportApiEndpoints } from './modules/shift-closing-report.api'
import { channelApiEndpoints } from './modules/channel.api'
import { saleOnlineApiEndpoints } from './modules/sale-online.api'
import { webviewApiEndpoints } from './modules/webview.api'
import { contractApiEndpoints } from './modules/contract.api'
import { offboardingApiEndpoints } from './modules/offboarding.api'
import type {
  ApiEndpointDefinition,
  ApiEndpointRegistry,
  ApiPathParamValue,
  ApiQueryValue,
  BuildApiPathOptions,
} from './types'

export type {
  ApiEndpointDefinition,
  ApiEndpointRegistry,
  ApiHttpMethod,
  ApiModuleEndpoints,
  ApiPathParams,
  ApiQueryParams,
  BuildApiPathOptions,
} from './types'

export const apiEndpoints = {
  billiardLight: billiardLightApiEndpoints,
  activityLog: activityLogApiEndpoints,
  areaPricing: areaPricingApiEndpoints,
  auth: authApiEndpoints,
  booking: bookingApiEndpoints,
  branch: branchApiEndpoints,
  cashBook: cashBookApiEndpoints,
  cashTransaction: cashTransactionApiEndpoints,
  dashboard: dashboardApiEndpoints,
  deliveryPartner: deliveryPartnerApiEndpoints,
  inventory: inventoryApiEndpoints,
  manufacturing: manufacturingApiEndpoints,
  offlinePos: offlinePosApiEndpoints,
  customer: customerApiEndpoints,
  paymentMethod: paymentMethodApiEndpoints,
  permission: permissionApiEndpoints,
  product: productApiEndpoints,
  price: priceApiEndpoints,
  role: roleApiEndpoints,
  salesArea: salesAreaApiEndpoints,
  salesInvoice: salesInvoiceApiEndpoints,
  saleReturn: saleReturnApiEndpoints,
  salesOrder: salesOrderApiEndpoints,
  salesTable: salesTableApiEndpoints,
  staff: staffApiEndpoints,
  supplier: supplierApiEndpoints,
  systemSetting: systemSettingApiEndpoints,
  printTemplate: printTemplateApiEndpoints,
  tablePricing: tablePricingApiEndpoints,
  tableSession: tableSessionApiEndpoints,
  voucher: voucherApiEndpoints,
  shiftRevenueReport: shiftRevenueReportApiEndpoints,
  shiftClosingReport: shiftClosingReportApiEndpoints,
  channel: channelApiEndpoints,
  saleOnline: saleOnlineApiEndpoints,
  webview: webviewApiEndpoints,
  contract: contractApiEndpoints,
  offboarding: offboardingApiEndpoints,
} as const satisfies ApiEndpointRegistry

export type ApiModuleName = keyof typeof apiEndpoints
export type ApiEndpointName<TModule extends ApiModuleName> = keyof (typeof apiEndpoints)[TModule]

function encodePathParam(value: ApiPathParamValue) {
  return encodeURIComponent(String(value))
}

function appendQueryValue(searchParams: URLSearchParams, key: string, value: ApiQueryValue) {
  if (value === null || value === undefined) {
    return
  }

  searchParams.append(key, String(value))
}

function buildQueryString(query: BuildApiPathOptions['query']) {
  const searchParams = new URLSearchParams()

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => appendQueryValue(searchParams, key, item))
      return
    }

    appendQueryValue(searchParams, key, value)
  })

  const queryString = searchParams.toString()

  return queryString ? `?${queryString}` : ''
}

export function buildApiPath(endpoint: ApiEndpointDefinition, options: BuildApiPathOptions = {}) {
  const path = endpoint.path.replace(/:([a-zA-Z0-9_]+)/g, (_, key: string) => {
    const value = options.params?.[key]

    if (value === null || value === undefined) {
      throw new Error(`Missing API path param: ${key}`)
    }

    return encodePathParam(value)
  })

  return `${path}${buildQueryString(options.query)}`
}

export function getApiEndpoint<
  TModule extends ApiModuleName,
  TEndpoint extends ApiEndpointName<TModule>,
>(moduleName: TModule, endpointName: TEndpoint) {
  return apiEndpoints[moduleName][endpointName]
}

export function listApiModules() {
  return Object.keys(apiEndpoints) as ApiModuleName[]
}
